import * as THREE from 'three';
import '../ui/fortress.css';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { DungeonEnemy } from '../entities/dungeonEnemy.js';
import { HUD } from '../ui/hud.js';
import { getStat, trainStat, addCrumbs, setFlag, getFlag } from '../core/save.js';
import { addFound, hasFound } from '../core/collection.js';

// The Fortress dungeon crawl — deeper floors behind the sealed inner door.
// A short linear gauntlet: entrance hall, monster room, key room, treasure room,
// then an exit back to the Fortress hall. Tap an enemy during its telegraph to
// swing; miss the window and it hits you.

export function createFortressDungeonScene(){
  return () => new FortressDungeonScene();
}

const STONE   = 0x7C776C;
const STONE_D = 0x5E5A50;
const MORTAR  = 0x46433B;
const WOOD    = 0x5A3A22;
const GOLD    = 0xE8C24B;

class FortressDungeonScene {
  constructor(){
    this.state = 'explore';
    this.panels = [];
    this.vignette = null;
    this.combatToastT = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1A1016);
    this.scene.fog = new THREE.Fog(0x1A1016, 14, 46);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 13;
    this.camTarget = new THREE.Vector3(0, 1.6, 4);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildDungeon();
    this._buildDecor();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 8);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.enemies = [
      new DungeonEnemy('golem'),
      new DungeonEnemy('golem')
    ];
    this.enemies[0].root.position.set(-1.8, 0, -2.5);
    this.enemies[1].root.position.set( 1.8, 0, -3.8);
    this.enemies.forEach(e => this.scene.add(e.root));

    this.hasKey = false;
    this.chestOpen = false;
    this.playerHp = 3;
    this.playerMaxHp = 3;
    this.swingCooldown = 0;
    this.invulnT = 0;

    this.hotspots = [
      { pos: new THREE.Vector3(0, 0, -8.5), r: 1.6, label: '🗝 pick up the old key', kind: 'key', action: () => this._takeKey() },
      { pos: new THREE.Vector3(0, 0, -15.5), r: 1.8, label: '📦 open the hoard chest', kind: 'chest', action: () => this._openChest() },
      // exit appears after the hoard chest is opened
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0x4A3A42, 0.7));
    const moon = new THREE.DirectionalLight(0x7A8AB0, 0.55);
    moon.position.set(4, 14, 8); moon.castShadow = true;
    moon.shadow.mapSize.set(2048, 2048);
    const s = 18;
    moon.shadow.camera.left = -s; moon.shadow.camera.right = s;
    moon.shadow.camera.top = s; moon.shadow.camera.bottom = -s;
    this.scene.add(moon);
  }

  _buildDungeon(){
    const HALF_W = 6, Z0 = -26, Z1 = 11;
    this.roomBounds = { halfW: HALF_W, z0: Z0, z1: Z1 };
    const len = Z1 - Z0, midZ = (Z0 + Z1) / 2;

    // floor
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, len), new THREE.MeshLambertMaterial({ color: STONE }));
    floor.rotation.x = -Math.PI/2; floor.position.set(0, 0, midZ); floor.receiveShadow = true;
    this.scene.add(floor);

    // mortar grid
    for (let x = -HALF_W + 2; x < HALF_W; x += 2){
      const line = box(0.08, 0.02, len, MORTAR, x, 0.02, midZ, this.scene);
      line.castShadow = false;
    }
    for (let z = Z0 + 2; z < Z1; z += 2){
      const line = box(HALF_W * 2, 0.02, 0.08, MORTAR, 0, 0.02, z, this.scene);
      line.castShadow = false;
    }

    // side walls
    const wallMat = new THREE.MeshLambertMaterial({ color: STONE_D });
    const wallGeo = new THREE.PlaneGeometry(len, 8);
    const wallL = new THREE.Mesh(wallGeo, wallMat);
    wallL.rotation.y = Math.PI/2; wallL.position.set(-HALF_W, 4, midZ); wallL.receiveShadow = true;
    this.scene.add(wallL);
    const wallR = new THREE.Mesh(wallGeo, wallMat);
    wallR.rotation.y = -Math.PI/2; wallR.position.set(HALF_W, 4, midZ); wallR.receiveShadow = true;
    this.scene.add(wallR);

    // room dividers: shallow arches marking each chamber
    this._arch(0, 4.2);          // entrance -> monster room
    this._arch(0, -6.2);         // monster -> key room
    this._arch(0, -12.2);        // key -> treasure room
    this._arch(0, -19.2);        // treasure -> exit hall

    // back wall at far end
    box(HALF_W * 2, 8, 0.4, STONE_D, 0, 4, Z0, this.scene);

    // ceiling
    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, len), new THREE.MeshLambertMaterial({ color: 0x1A1218 }));
    ceil.rotation.x = Math.PI/2; ceil.position.set(0, 8, midZ); this.scene.add(ceil);

    // sconces
    const sconces = [ {x:-5.5,z:4}, {x:5.5,z:4}, {x:-5.5,z:-10}, {x:5.5,z:-10}, {x:-5.5,z:-20}, {x:5.5,z:-20} ];
    this.sconces = [];
    sconces.forEach(s => {
      const g = new THREE.Group(); g.position.set(s.x, 3.4, s.z); this.scene.add(g);
      cyl(0.04, 0.04, 0.4, 0x5A3A22, s.x < 0 ? 0.18 : -0.18, 0, 0, g, 6);
      const flame = new THREE.Group(); flame.position.set(s.x < 0 ? 0.42 : -0.42, 0.28, 0); g.add(flame);
      const c1 = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.42, 8), new THREE.MeshBasicMaterial({ color: 0xFF9A3C }));
      c1.position.y = 0.18; flame.add(c1);
      const light = new THREE.PointLight(0xFF9A3C, 0.7, 8, 2);
      light.position.set(s.x < 0 ? s.x + 0.5 : s.x - 0.5, 3.8, s.z); this.scene.add(light);
      this.sconces.push({ flame, light, phase: Math.random() * 6 });
    });
  }

  _arch(x, z){
    const w = this.roomBounds.halfW * 2 - 1.2;
    box(1.0, 4.2, 0.35, STONE, x - w/2 - 0.5, 2.1, z, this.scene);
    box(1.0, 4.2, 0.35, STONE, x + w/2 + 0.5, 2.1, z, this.scene);
    box(w + 2.2, 0.5, 0.4, STONE, x, 4.4, z, this.scene);
  }

  _buildDecor(){
    // rubble piles
    for (let i = 0; i < 6; i++){
      const x = (Math.random() - 0.5) * 8;
      const z = -2 - Math.random() * 18;
      sphere(0.2 + Math.random() * 0.15, 0x5E5A50, x, 0.15, z, this.scene, 7);
    }
    // the key pedestal
    this.keyGroup = new THREE.Group(); this.keyGroup.position.set(0, 0, -8.5); this.scene.add(this.keyGroup);
    box(0.7, 1.0, 0.7, STONE, 0, 0.5, 0, this.keyGroup);
    this.keyMesh = new THREE.Group();
    cyl(0.04, 0.04, 0.5, GOLD, 0, 0.6, 0, this.keyMesh, 6).rotation.z = Math.PI/2;
    cyl(0.035, 0.035, 0.18, GOLD, 0.22, 0.6, 0, this.keyMesh, 6).rotation.x = Math.PI/2;
    this.keyGroup.add(this.keyMesh);

    // the treasure chest
    this.chestGroup = new THREE.Group(); this.chestGroup.position.set(0, 0, -15.5); this.scene.add(this.chestGroup);
    box(1.8, 0.9, 1.0, WOOD, 0, 0.45, 0, this.chestGroup);
    box(1.9, 0.12, 1.1, GOLD, 0, 0.45, 0, this.chestGroup);
    box(0.14, 0.9, 1.1, GOLD, -0.8, 0.45, 0, this.chestGroup);
    box(0.14, 0.9, 1.1, GOLD, 0.8, 0.45, 0, this.chestGroup);
    const lock = box(0.24, 0.28, 0.1, GOLD, 0, 0.5, 0.56, this.chestGroup);
    this.chestLock = lock;

    // exit portal glow
    const exitGlow = new THREE.PointLight(GOLD, 0.6, 8, 2);
    exitGlow.position.set(0, 2.5, -23.5); this.scene.add(exitGlow);
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.vignette = document.createElement('div');
    this.vignette.className = 'fortress-vignette';
    document.body.appendChild(this.vignette);
    this._positionCamera();
    this.bread.setLantern(hasFound('ghost-lamp') ? 1.1 : 0);
    this._toExplore(true);
    this._buildCombatHud();
  }

  exit(){
    this._clearPanels();
    if (this.vignette){ this.vignette.remove(); this.vignette = null; }
    if (this.hudEl){ this.hudEl.remove(); this.hudEl = null; }
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _positionCamera(){
    this.azimuth = this.input?.azimuthGoal ?? 0;
    this.polar   = this.input?.polarGoal ?? 0.5;
    const b = this.roomBounds;
    this.camTarget.set(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1);
    this.camTarget.z = Math.max(b.z0 + 2, Math.min(b.z1 - 2, this.camTarget.z));
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this._clampCamera();
    this.camera.lookAt(this.camTarget);
  }

  _clampCamera(){
    const b = this.roomBounds;
    const pad = 1.5;
    this.camera.position.x = Math.max(-b.halfW + pad, Math.min(b.halfW - pad, this.camera.position.x));
    this.camera.position.z = Math.max(b.z0 + pad, Math.min(b.z1 - pad, this.camera.position.z));
    this.camera.position.y = Math.max(2, Math.min(9, this.camera.position.y));
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('fortress depths');
    if (first) HUD.say('The inner door groans open. Something doughy and ancient stirs in the dark.');
    HUD.setHint('tap the ground to walk · tap an enemy during its flash to strike · watch for attacks');
    HUD.hideHintSoon(9000);
  }

  _buildCombatHud(){
    this.hudEl = document.createElement('div');
    this.hudEl.className = 'dungeon-hud';
    this.hudEl.innerHTML = `
      <div class="dungeon-hp"><span id="dungeon-hp-bar" class="ok" style="width:100%"></span></div>
      <div id="dungeon-key" class="dungeon-key">🗝</div>
    `;
    document.body.appendChild(this.hudEl);
    this.hpBar = this.hudEl.querySelector('#dungeon-hp-bar');
    this.keyIcon = this.hudEl.querySelector('#dungeon-key');
  }

  _updateHpBar(){
    if (!this.hpBar) return;
    const pct = Math.max(0, (this.playerHp / this.playerMaxHp) * 100);
    this.hpBar.style.width = pct + '%';
    this.hpBar.className = pct > 60 ? 'ok' : pct > 30 ? 'warn' : 'crit';
  }

  _combatToast(text){
    this.combatToastT = 1.2;
    if (!this.toastEl){
      this.toastEl = document.createElement('div');
      this.toastEl.className = 'dungeon-toast';
      document.body.appendChild(this.toastEl);
    }
    this.toastEl.textContent = text;
    this.toastEl.style.opacity = '1';
  }

  // ---- interactions ----
  _tap(ray){
    if (this.state !== 'explore') return;

    // first, try to swing at an enemy
    if (this.swingCooldown <= 0 && this._checkEnemyClick(ray)) return;

    // otherwise walk
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-5, Math.min(5, gp.x));
    gp.z = Math.max(-25, Math.min(10, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _checkEnemyClick(ray){
    const targets = this.enemies.filter(e => !e.dead).map(e => e.hitTarget);
    if (!targets.length) return false;
    const hits = ray.intersectObjects(targets, false);
    if (hits.length){
      const enemy = hits[0].object.userData.enemy;
      if (enemy) this._swingAt(enemy);
      return true;
    }
    return false;
  }

  _swingAt(enemy){
    this.bread.stop();
    this.bread.startPose('strike', 0.35);
    this.bread.drawFace('effort');
    this.swingCooldown = 0.45;

    // damage scales with STR
    const dmg = 1 + Math.floor(getStat('str') * 0.5);
    const killed = enemy.takeDamage(dmg);
    this._combatToast(killed ? 'crumbled!' : `hit -${dmg}`);

    if (killed){
      // small crumb reward per golem
      addCrumbs(5);
      HUD.refreshCrumbs();
    }
  }

  _takeKey(){
    if (this.hasKey) return;
    if (this.enemies.some(e => !e.dead)){
      HUD.say('The golems are guarding the key! Take them down first.');
      return;
    }
    this.hasKey = true;
    this.keyMesh.visible = false;
    this._combatToast('picked up the old key');
    this.keyIcon.classList.add('active');
    HUD.say('An iron key, heavy with crumbs. The hoard chest should open now.');
  }

  _openChest(){
    if (this.chestOpen) return;
    if (!this.hasKey){
      HUD.say('Locked tight. There must be a key somewhere in these depths.');
      this.bread.drawFace('o');
      return;
    }
    this.chestOpen = true;
    this.chestLock.visible = false;
    this.bread.drawFace('yum');

    const CRUMBS = 80;
    addCrumbs(CRUMBS); HUD.refreshCrumbs();
    const firstTime = !hasFound('relic-gauntlet');
    addFound('relic-gauntlet');
    setFlag('fortressDungeonCleared', true);
    const strGain = trainStat('str', 1);
    if (strGain > 0) HUD.refreshStats();

    // unlock the exit to the throne
    this.hotspots.push({ pos: new THREE.Vector3(0, 0, -23.5), r: 1.8, label: '🚪 exit to the King\'s Chamber', kind: 'exit', action: () => this._exit() });

    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">👊</div>
        <h2>Hoard Claimed</h2>
        <div class="cafe-sub">the Baker King's armory</div>
        <p class="fortress-loot">🛡️ Baker King's Gauntlet</p>
        <p class="fortress-note">${firstTime ? 'a relic of crust and steel — added to your collection!' : 'the gauntlet still fits.'}</p>
        <p class="cafe-payout">🍞 +${CRUMBS} crumbs${strGain > 0 ? ` · +${strGain} STR` : ''}</p>
        <button class="cafe-btn ghost close">huzzah!</button>
      </div>`);
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _exit(){
    this.manager.goTo('fortressThrone', { from: 'fortressDungeon' });
  }

  _defeat(){
    this.state = 'defeat';
    this.input.onTap = null;
    HUD.say('Toast crumbled under the assault… you wake up back in the Forest.');
    setTimeout(() => {
      this.manager.goTo('forest', { from: 'fortressDungeon', defeated: true });
    }, 2200);
  }

  // ---- panels ----
  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- loop ----
  update(dt){
    const t = performance.now() * 0.001;

    // flicker sconces
    this.sconces.forEach((s, i) => {
      s.light.intensity = 0.55 + 0.18 * Math.sin(t * 15 + s.phase) + 0.08 * Math.sin(t * 31 + i);
      s.flame.scale.y = 0.9 + 0.18 * Math.sin(t * 20 + s.phase);
    });

    // swing cooldown
    if (this.swingCooldown > 0) this.swingCooldown -= dt;

    // enemy AI + combat resolution
    const playerPos = this.bread.root.position;
    this.invulnT = Math.max(0, this.invulnT - dt);

    this.enemies.forEach(e => {
      if (e.dead) return;
      const d = e.root.position.distanceTo(playerPos);

      // idle enemies telegraph when player is close
      if (e.state === 'idle' && d < 5.5) e.startAttack();

      e.update(dt, playerPos);

      // if the enemy is lunging and close, it may hit the player
      if (e.state === 'attacking' && !e.dealtDamage && d < 1.5){
        e.dealtDamage = true;
        // AGI gives a small dodge chance (capped at ~40%)
        const dodgeChance = Math.min(0.4, getStat('agi') * 0.07);
        if (Math.random() < dodgeChance){
          this._combatToast('dodged!');
        } else if (this.invulnT <= 0){
          this.playerHp -= e.damage;
          this.invulnT = 0.8;
          this._updateHpBar();
          this._combatToast(`ouch! -${e.damage} HP`);
          this.bread.drawFace('o');
          if (this.playerHp <= 0) this._defeat();
        }
      }
      if (e.state !== 'attacking') e.dealtDamage = false;
    });

    // key float
    if (!this.hasKey && this.keyMesh){
      this.keyMesh.rotation.y += dt * 1.5;
      this.keyMesh.position.y = 0.6 + Math.sin(t * 2) * 0.08;
    }

    // combat toast fade
    if (this.combatToastT > 0){
      this.combatToastT -= dt;
      if (this.combatToastT <= 0 && this.toastEl) this.toastEl.style.opacity = '0';
    }

    this.bread.update(dt);

    // proximity hotspots
    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => {
        if (h.kind === 'key' && this.hasKey) return;
        if (h.kind === 'chest' && this.chestOpen) return;
        const d = h.pos.distanceTo(playerPos);
        if (d < nd){ nd = d; near = h; }
      });
      if (near && nd < near.r){
        if (this.nearSpot !== near){
          this.nearSpot = near;
          HUD.showEnter(near.label, () => near.action());
        }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    // orbit-follow camera
    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    const b = this.roomBounds;
    this.camTarget.lerp(new THREE.Vector3(playerPos.x * 0.5, 1.6, playerPos.z * 0.5 + 1), 0.06);
    this.camTarget.z = Math.max(b.z0 + 2, Math.min(b.z1 - 2, this.camTarget.z));
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this._clampCamera();
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
